import { useEffect, useRef, useState, useCallback } from 'react';
import { IS_BROWSER } from '@wconcept/wconcept-common/utils/baseUtils';

interface ScrollStore {
  manualScroll: boolean;
  pendingNaviClick: number | null;
  setManualScroll: (value: boolean) => void;
  setPendingNaviClick: (value: number | null) => void;
}

interface ScrollSectionPosition {
  readonly sectionNumber: number;
  readonly top: number;
}

type ScrollActivationOffset = number | (() => number);

export const resolveScrollActivationOffset = (
  offset: ScrollActivationOffset | undefined,
  viewportHeight: number
): number =>
  typeof offset === 'function' ? offset() : offset ?? viewportHeight * 0.3;

/**
 * 스크롤 기준선을 마지막으로 통과한 실제 앵커 ID를 반환한다.
 * 아래로 내릴 때는 다음 앵커까지 유지하고, 위로 올릴 때는 경계를 넘으면 이전 앵커로 돌아간다.
 */
export const getActiveSectionByScrollPosition = (
  sectionPositions: readonly ScrollSectionPosition[],
  activationPosition: number
): number | null => {
  const orderedSections = [...sectionPositions].sort((a, b) => a.top - b.top);
  if (orderedSections.length === 0) return null;

  return orderedSections.reduce(
    (activeSection, section) =>
      section.top <= activationPosition ? section.sectionNumber : activeSection,
    orderedSections[0].sectionNumber
  );
};

export const shouldSyncActiveSectionFromScroll = (
  manualScroll: boolean,
  pendingNavigation: number | null
): boolean => !manualScroll && pendingNavigation === null;

export const useScrollPositionNavi = (
  sectionNumbers: number[],
  initialActiveSection?: number,
  options?: {
    offset?: ScrollActivationOffset; // 활성화 오프셋 (기본: 화면 높이의 30%)
    throttle?: number; // 스크롤 이벤트 쓰로틀링 (기본: 16ms)
    scrollStore?: ScrollStore; // 스크롤 상태 스토어 (선택적 - 제공되면 스토어 사용, 없으면 로컬 ref 사용)
  }
) => {
  const [activeSection, setActiveSection] = useState(
    initialActiveSection || sectionNumbers[0]
  );
  // 스토어가 제공되면 스토어 사용, 없으면 로컬 ref 사용 (하위 호환성)
  const scrollStore = options?.scrollStore;
  const manualScrollRef = useRef(false);
  const sectionsRef = useRef<Map<number, HTMLElement>>(new Map());
  const pendingNaviClickRef = useRef<number | null>(null);

  // 스토어 또는 ref에서 값을 가져오는 헬퍼
  const getManualScroll = () =>
    scrollStore ? scrollStore.manualScroll : manualScrollRef.current;
  const getPendingNaviClick = () =>
    scrollStore ? scrollStore.pendingNaviClick : pendingNaviClickRef.current;
  const setManualScroll = (value: boolean) => {
    if (scrollStore) {
      scrollStore.setManualScroll(value);
    } else {
      manualScrollRef.current = value;
    }
  };
  const setPendingNaviClick = (value: number | null) => {
    if (scrollStore) {
      scrollStore.setPendingNaviClick(value);
    } else {
      pendingNaviClickRef.current = value;
    }
  };

  const config = {
    offset: options?.offset ?? (IS_BROWSER ? window.innerHeight * 0.3 : 300),
    throttle: options?.throttle ?? 16,
  };

  // 섹션 요소들 찾아서 캐싱
  const cacheSections = useCallback(() => {
    const sections = new Map<number, HTMLElement>();

    sectionNumbers.forEach(num => {
      const element = document.getElementById(`section${num}`);
      if (element) {
        sections.set(num, element);
      }
    });

    sectionsRef.current = sections;
    return sections.size > 0;
  }, [sectionNumbers]);

  // 현재 스크롤 위치에서 활성 섹션 계산
  const getCurrentSection = useCallback(() => {
    if (!IS_BROWSER) return null;

    const { scrollY } = window;
    const sections = sectionsRef.current;

    if (sections.size === 0) return null;

    const sectionPositions = Array.from(
      sections,
      ([sectionNumber, element]) => ({
        sectionNumber,
        top: scrollY + element.getBoundingClientRect().top,
      })
    );

    return getActiveSectionByScrollPosition(
      sectionPositions,
      scrollY + resolveScrollActivationOffset(config.offset, window.innerHeight)
    );
  }, [config.offset]);

  // 스크롤 이벤트 핸들러 (쓰로틀링 적용)
  const handleScroll = useCallback(() => {
    if (!IS_BROWSER || getManualScroll() || getPendingNaviClick() !== null)
      return;

    const currentSection = getCurrentSection();
    if (currentSection !== null && currentSection !== activeSection) {
      setActiveSection(currentSection);
    }
  }, [getCurrentSection, activeSection, scrollStore]);

  // 쓰로틀링된 스크롤 핸들러
  const throttledHandleScroll = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout | null = null;

      return () => {
        if (!IS_BROWSER || timeoutId) return;

        timeoutId = setTimeout(() => {
          handleScroll();
          timeoutId = null;
        }, config.throttle);
      };
    })(),
    [handleScroll, config.throttle]
  );

  // 네비게이션 클릭 핸들러
  const handleNaviClick = useCallback(
    (sectionNumber: number) => {
      setManualScroll(true);
      setPendingNaviClick(sectionNumber);
      // setActiveSection(sectionNumber);

      // 스크롤 완료 후 자동 감지 재개
      // setTimeout(() => {
      //   setManualScroll(false);
      //   setPendingNaviClick(null);
      // }, 1000);
    },
    [scrollStore]
  );

  // 초기화 및 스크롤 이벤트 등록
  useEffect(() => {
    if (!IS_BROWSER) return;

    const initWithRetry = () => {
      const success = cacheSections();
      if (!success) {
        setTimeout(initWithRetry, 300);
      } else {
        // 초기 섹션 설정
        const currentSection = getCurrentSection();
        // 네비 클릭 이동 대기 중에는 현재 화면의 앵커로 선택 상태를 덮어쓰지 않는다.
        if (
          currentSection !== null &&
          shouldSyncActiveSectionFromScroll(
            getManualScroll(),
            getPendingNaviClick()
          )
        ) {
          setActiveSection(currentSection);
        }
      }
    };

    const timer = setTimeout(initWithRetry, 100);

    // 스크롤 이벤트 등록
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', throttledHandleScroll, {
        passive: true,
      });
      window.addEventListener('resize', cacheSections, { passive: true });
    }

    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timer);
      if (typeof window !== 'undefined') {
        window.removeEventListener('scroll', throttledHandleScroll);
        window.removeEventListener('resize', cacheSections);
      }
    };
  }, [cacheSections, getCurrentSection, throttledHandleScroll]);

  return {
    activeSection,
    setActiveSection,
    handleNaviClick,
    reinitialize: cacheSections,
    // 하위 호환성: ref 형태로 제공 (스토어 사용 시에도 동일한 인터페이스)
    manualScrollRef: scrollStore
      ? ({
          current: scrollStore.manualScroll,
          get value() {
            return scrollStore.manualScroll;
          },
          set value(v: boolean) {
            scrollStore.setManualScroll(v);
          },
        } as React.MutableRefObject<boolean>)
      : manualScrollRef,
    pendingNaviClickRef: scrollStore
      ? ({
          current: scrollStore.pendingNaviClick,
          get value() {
            return scrollStore.pendingNaviClick;
          },
          set value(v: number | null) {
            scrollStore.setPendingNaviClick(v);
          },
        } as React.MutableRefObject<number | null>)
      : pendingNaviClickRef,
    // 스토어 사용 시 편의를 위한 직접 접근 함수
    setManualScroll,
    setPendingNaviClick,
  };
};
